import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/errorLogger";
import { setSentryUser } from "@/lib/sentry";
import { clearSessionTracking } from "@/lib/deviceSession";

export type AppRole = "super_admin" | "admin" | "front_desk" | "editor" | "viewer";

export interface UserProfile {
  first_name: string;
  last_name: string;
  email: string;
  job_title: string;
  profile_photo_url: string | null;
  onboarding_completed: boolean;
  onboarding_completed_at: string | null;
  onboarding_choice_made_at: string | null;
  invited_by: string | null;

  is_deactivated: boolean;
  onboarding_tour_completed: boolean;
  onboarding_tour_completed_at: string | null;
  delta_tours_completed: Record<string, string>;
  nursery_id: string | null;
  explore_return_nursery_id: string | null;
  nursery_plan: "starter" | "professional" | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: AppRole | null;
  profile: UserProfile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  handleAuthError: (error: unknown) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  role: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  handleAuthError: async () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const authHydrationId = useRef(0);

  const fetchUserData = async (userId: string) => {
    try {
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();

      setRole((roleData?.role as AppRole) ?? null);

      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name, email, job_title, profile_photo_url, onboarding_completed, onboarding_completed_at, onboarding_choice_made_at, invited_by, is_deactivated, nursery_id, explore_return_nursery_id, onboarding_tour_completed, onboarding_tour_completed_at, delta_tours_completed")
        .eq("user_id", userId)
        .maybeSingle();

      if (profileData?.is_deactivated) {
        await supabase.auth.signOut({ scope: "local" });
        setSession(null);
        setUser(null);
        setRole(null);
        setProfile(null);
        window.location.href = "/login?message=Your+access+to+Nursly+has+been+updated.+Please+speak+to+your+manager.";
        return;
      }

      // Identify user in Sentry (no PII - id + role + nursery + plan only)
      let plan: "starter" | "professional" | null = null;
      try {
        if (profileData?.nursery_id) {
          const { data: nurseryData } = await supabase
            .from("nurseries")
            .select("plan")
            .eq("id", profileData.nursery_id)
            .maybeSingle();
          plan = (nurseryData?.plan as "starter" | "professional" | null) ?? null;
        }
        setSentryUser({
          id: userId,
          role: (roleData?.role as string) ?? "unknown",
          nursery_id: profileData?.nursery_id ?? undefined,
          plan: plan ?? undefined,
        });
      } catch {
        setSentryUser({ id: userId, role: (roleData?.role as string) ?? "unknown" });
      }

      setProfile(
        profileData
          ? ({
              ...profileData,
              delta_tours_completed: (profileData.delta_tours_completed as Record<string, string>) ?? {},
              nursery_plan: plan,
            } as UserProfile)
          : null
      );

      // Legacy migration (B3-01): older builds tracked tour completion in
      // localStorage (per-device), which caused the tour to re-trigger on
      // other browsers. If a legacy flag is present, copy it into the
      // profile row (single source of truth) and clear the local key.
      try {
        if (profileData && !profileData.onboarding_tour_completed && typeof window !== "undefined") {
          const legacyKeys = [
            "nursly_tour_completed",
            "nursly_onboarding_tour_completed",
            `nursly_tour_completed_${userId}`,
          ];
          const hasLegacy = legacyKeys.some(
            k => window.localStorage.getItem(k) === "1" || window.localStorage.getItem(k) === "true"
          );
          if (hasLegacy) {
            await supabase
              .from("profiles")
              .update({
                onboarding_tour_completed: true,
                onboarding_tour_completed_at: new Date().toISOString(),
              })
              .eq("user_id", userId);
            legacyKeys.forEach(k => window.localStorage.removeItem(k));
          }
        }
      } catch {
        // best-effort; never block sign-in
      }

      // Record that this person has actually signed in, so Access Management
      // shows a true "Active" status and a real last sign-in time instead of
      // leaving them stuck on "Invite Pending". Once per browser session.
      try {
        if (typeof window !== "undefined") {
          const key = `nursly_login_recorded_${userId}`;
          if (window.sessionStorage.getItem(key) !== "1") {
            await supabase.rpc("record_my_login");
            window.sessionStorage.setItem(key, "1");
          }
        }
      } catch {
        // best-effort; never block sign-in
      }
    } catch (err) {
      logError(err, "AuthContext.fetchUserData", userId);
    }
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  }, [user]);

  const handleAuthError = useCallback(async (error: unknown): Promise<boolean> => {
    try {
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !data.session) {
        await supabase.auth.signOut({ scope: "local" });
        window.location.href = "/login?message=Your+session+expired+%E2%80%94+please+log+in+again+to+continue.";
        return false;
      }
      setSession(data.session);
      setUser(data.session.user);
      return true;
    } catch {
      window.location.href = "/login?message=Your+session+expired+%E2%80%94+please+log+in+again+to+continue.";
      return false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const hydrateSignedInUser = (nextSession: Session, source: string) => {
      const hydrationId = ++authHydrationId.current;
      setLoading(true);
      setSession(nextSession);
      setUser(nextSession.user);

      // Supabase advises not to run additional Supabase calls synchronously
      // inside onAuthStateChange. Defer the profile/role hydration, but do not
      // release ProtectedRoute until hydration has actually completed.
      setTimeout(async () => {
        try {
          await fetchUserData(nextSession.user.id);
        } catch (err) {
          logError(err, `AuthContext.${source}`, nextSession.user.id);
        } finally {
          if (mounted && authHydrationId.current === hydrationId) {
            setLoading(false);
          }
        }
      }, 0);
    };

    const hydrateSignedOutUser = () => {
      ++authHydrationId.current;
      setSession(null);
      setUser(null);
      setRole(null);
      setProfile(null);
      setSentryUser(null);
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          hydrateSignedInUser(session, "onAuthStateChange");
        } else {
          hydrateSignedOutUser();
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session?.user) {
        hydrateSignedInUser(session, "getSession");
      } else {
        hydrateSignedOutUser();
      }
    }).catch((err) => {
      logError(err, "AuthContext.getSession", null);
      if (mounted) hydrateSignedOutUser();
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    // Keep the device's own/shared answer, but reset the timers so the next
    // sign-in starts fresh.
    clearSessionTracking();
    // The next person signing in on this tab gets a clean setup check.
    try { sessionStorage.removeItem("nursly.onboarding.done"); } catch { /* private mode */ }
    await supabase.auth.signOut({ scope: "local" });
    setSession(null);
    setUser(null);
    setRole(null);
    setProfile(null);
    setSentryUser(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, role, profile, loading, signOut, refreshProfile, handleAuthError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
